import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CreateSubscription from './CreateSubscription';
import ViewSubscriptionDrawer from './ViewSubscriptionDrawer';

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-gray-100 text-gray-800',
};

const SubscriptionsTable = () => {
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const fetchSubscriptions = async () => {
    try {
      const res = await api.get('/api/subscriptions');
      setSubscriptions(res.data);
      setFilteredSubscriptions(res.data);
    } catch (err) {
      console.error('Error al cargar suscripciones:', err);
      setError('No se pudieron cargar las suscripciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    let result = subscriptions;
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.user_name.toLowerCase().includes(lower) ||
          s.email?.toLowerCase().includes(lower) ||
          s.plan_name?.toLowerCase().includes(lower)
      );
    }
    if (statusFilter) {
      result = result.filter((s) => s.status === statusFilter);
    }
    setFilteredSubscriptions(result);
  }, [searchTerm, statusFilter, subscriptions]);

  return (
    <div className="bg-gray-50 min-h-screen py-4 px-4 sm:px-6 lg:px-8 font-[Manrope]">
      <div className="mx-auto max-w-7xl">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Suscripciones</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">
                Lista de todas las suscripciones activas de los usuarios y servicios asociados.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              >
                + Suscripción
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="flex flex-col sm:flex-row sm:gap-4 mb-6 gap-4">
            <input
              type="text"
              placeholder="Buscar por nombre, correo o plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-xs sm:text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-2 py-2 text-xs sm:text-sm w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activa</option>
              <option value="pending">Pendiente</option>
              <option value="inactive">Inactiva</option>
            </select>
          </div>

          {/* Content */}
          {loading ? (
            <p className="text-xs sm:text-sm text-gray-500 text-center py-8">Cargando...</p>
          ) : error ? (
            <p className="text-xs sm:text-sm text-red-600 text-center py-8">{error}</p>
          ) : filteredSubscriptions.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm sm:text-base font-semibold text-gray-900">
                No hay suscripciones activas
              </h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                Aún no se han registrado suscripciones.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setIsDrawerOpen(true)}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                >
                  + Suscripción
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-2 py-3 sm:px-4">Usuario</th>
                      <th className="px-2 py-3 sm:px-4">Servicio</th>
                      <th className="px-2 py-3 sm:px-4">Plan</th>
                      <th className="px-2 py-3 sm:px-4 hidden md:table-cell">Inicio</th>
                      <th className="px-2 py-3 sm:px-4 hidden lg:table-cell">Próximo cobro</th>
                      <th className="px-2 py-3 sm:px-4">Estado</th>
                      <th className="px-2 py-3 sm:px-4">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredSubscriptions.map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="px-2 py-3 sm:px-4 whitespace-nowrap">{s.user_name}</td>
                        <td className="px-2 py-3 sm:px-4 whitespace-nowrap">{s.service_name}</td>
                        <td className="px-2 py-3 sm:px-4">{s.plan_name}</td>
                        <td className="px-2 py-3 sm:px-4 hidden md:table-cell whitespace-nowrap">
                          {s.start_date?.split('T')[0] || '-'}
                        </td>
                        <td className="px-2 py-3 sm:px-4 hidden lg:table-cell whitespace-nowrap">
                          {s.next_billing_date?.split('T')[0] || '-'}
                        </td>
                        <td className="px-2 py-3 sm:px-4">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                              STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="px-2 py-3 sm:px-4">
                          <button
                            className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
                            onClick={() => setSelectedSubscription(s)}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4">
                {filteredSubscriptions.map((s) => (
                  <div
                    key={s.id}
                    className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">{s.user_name}</span>
                        <button
                          className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm"
                          onClick={() => setSelectedSubscription(s)}
                        >
                          Editar
                        </button>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Servicio: </span>
                        <span className="text-xs text-gray-700">{s.service_name}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Plan: </span>
                        <span className="text-xs text-gray-700">{s.plan_name}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Inicio: </span>
                        <span className="text-xs text-gray-700">{s.start_date?.split('T')[0] || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Próximo cobro: </span>
                        <span className="text-xs text-gray-700">{s.next_billing_date?.split('T')[0] || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600">Estado: </span>
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                            STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {s.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawer creación */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998] flex justify-end">
          <div className="bg-white w-full max-w-xs sm:max-w-md h-full overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Crear Suscripción</h2>
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="text-gray-600 hover:text-gray-800 text-lg sm:text-xl"
              >
                ✕
              </button>
            </div>
            <CreateSubscription onClose={() => setIsDrawerOpen(false)} />
          </div>
        </div>
      )}

      {/* Drawer edición */}
      {selectedSubscription && (
        <ViewSubscriptionDrawer
          subscription={selectedSubscription}
          onClose={() => setSelectedSubscription(null)}
          onSubscriptionUpdated={(updated) => {
            setSubscriptions((prev) =>
              prev.map((s) => (s.id === updated.id ? updated : s))
            );
            setSelectedSubscription(updated);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionsTable;